import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, Input, Select, DatePicker, Col, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { PlusCircleFilled, CalendarOutlined } from '@ant-design/icons';
import Button from '../../common/Button';
import { Drawer } from '../../common/Drawer';
import { COLORS, TEXT_SIZES } from '../../../utils/constants';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { defaultCategories, SportLiteType } from '../../../types/types';
import { TournamentType } from '../../../enums/enums';
import { getSportsRequest } from '../../../api/requests/sports';
import { GenderKeyToEnum } from '../../../utils/eventUtils';
import { createTournamentRequest } from '../../../api/requests/tournaments';

const { Option } = Select;

interface Props {
  visible: boolean;
  eventId: number;
  onClose: () => void;
  onSave: () => void;
  eventStartDate?: string;
  eventEndDate?: string;
}

const AddTournamentDrawer: React.FC<Props> = ({
  visible,
  onClose,
  onSave,
  eventId,
  eventStartDate,
  eventEndDate,
}) => {
  const [form] = Form.useForm();
  const snackbar = useSnackbar();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [sportsData, setSportsData] = useState<SportLiteType[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [selectedGender, setSelectedGender] = useState<string | undefined>();
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [showCustomCategoryInput, setShowCustomCategoryInput] =
    useState<boolean>(false);
  const [newCustomCategory, setNewCustomCategory] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [categoryError, setCategoryError] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const fetchSports = async () => {
    try {
      const res = await getSportsRequest();
      setSportsData(res?.value ?? []);
    } catch (err) {
      message.error('Failed to load sports');
    }
  };

  const selectedSport = useMemo(
    () => sportsData.find((s) => s.sportId === selectedSportId),
    [sportsData, selectedSportId],
  );

  const isTeamSport = useMemo(() => {
    return selectedSport?.isTeamSport ?? false;
  }, [selectedSport]);

  const genderOptions = useMemo(() => {
    if (isTeamSport)
      return [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ];
    return [
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' },
      { label: 'Mixed', value: 'mixed' },
    ];
  }, [isTeamSport]);

  const categoryOptions = useMemo(
    () => [...defaultCategories, ...customCategories],
    [customCategories],
  );

  const sportOptions = useMemo(
    () =>
      (sportsData || []).map((s) => ({ label: s.sportName, value: s.sportId })),
    [sportsData],
  );

  const uiTournamentTypeOptions = useMemo(() => {
    if (selectedSportId === null || selectedSportId === undefined) {
      return [
        { label: 'Singles', value: TournamentType.Singles },
        { label: 'Doubles', value: TournamentType.Doubles },
        { label: 'Mixed Doubles', value: TournamentType.MixedDoubles },
        { label: 'Team', value: TournamentType.Team },
      ];
    }
    if (isTeamSport) {
      return [{ label: 'Team', value: TournamentType.Team }];
    }

    if (selectedGender === 'mixed') {
      return [{ label: 'Mixed Doubles', value: TournamentType.MixedDoubles }];
    }

    if (selectedGender === 'male' || selectedGender === 'female') {
      return [
        { label: 'Singles', value: TournamentType.Singles },
        { label: 'Doubles', value: TournamentType.Doubles },
      ];
    }
    return [
      { label: 'Singles', value: TournamentType.Singles },
      { label: 'Doubles', value: TournamentType.Doubles },
      { label: 'Mixed Doubles', value: TournamentType.MixedDoubles },
    ];
  }, [isTeamSport, selectedGender, selectedSportId]);

  const onSportChange = (value: unknown) => {
    const sportId = Number(value);
    setSelectedSportId(Number.isNaN(sportId) ? null : sportId);
    setSelectedGender(undefined);
    form.setFieldsValue({ gender: undefined, type: undefined });
  };

  const onGenderChange = (value: unknown) => {
    const genderValue = String(value);
    setSelectedGender(genderValue);
    form.setFieldsValue({ type: undefined });
  };

  const addCustomCategory = useCallback(() => {
    const val = newCustomCategory.trim();
    if (!val) {
      setCategoryError('Please enter a category');
      return;
    }
    const exists =
      customCategories.some((c) => c.toLowerCase() === val.toLowerCase()) ||
      defaultCategories.some((c) => c.toLowerCase() === val.toLowerCase());
    if (exists) {
      setCategoryError('This category already exists.');
      return;
    }
    snackbar.success('New custom category has been added successfully.');
    setCustomCategories((prev) => [...prev, val]);
    setNewCustomCategory('');
    setShowCustomCategoryInput(false);
    setCategoryError('');
  }, [newCustomCategory, customCategories, snackbar]);

  const handleSubmit = async () => {
    setHasSubmitted(true);
    try {
      const values = await form.validateFields();
      const startDate: Dayjs = values.startDate;
      const endDate: Dayjs = values.endDate;
      const payload = {
        eventId,
        sportId: values.sport,
        gender: GenderKeyToEnum[values.gender],
        customTournamentCategory: values.category,
        tournamentType: values.type,
        entryFee: Number(values.fee),
        startDate: startDate.startOf('day').toISOString(),
        endDate: endDate.endOf('day').toISOString(),
        rules: '',
        prizeStructure: '',
        tournamentName: '',
        tournamentDescription: '',
      };
      setIsSubmitting(true);
      await createTournamentRequest(payload);
      snackbar.success('Tournament created successfully');
      onSave();
      handleClose();
    } catch (err: any) {
      if (err.Error) {
        snackbar.error(err.Error);
      } else {
        console.error('Failed to create tournament');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = useMemo(() => dayjs().startOf('day'), []);

  const handleClose = () => {
    form.resetFields();
    setSelectedSportId(null);
    setCustomCategories([]);
    setNewCustomCategory('');
    setShowCustomCategoryInput(false);
    setCategoryError('');
    onClose();
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  //Event Limits
  const eventStartDayJS = useMemo(
    () => (eventStartDate ? dayjs(eventStartDate).startOf('day') : undefined),
    [eventStartDate],
  );

  const eventEndDayJS = useMemo(
    () => (eventEndDate ? dayjs(eventEndDate).endOf('day') : undefined),
    [eventEndDate],
  );

  useEffect(() => {
    if (visible) fetchSports();
    if (visible) {
      setTimeout(() => form.resetFields(), 0);
      setSelectedSportId(null);
      setCustomCategories([]);
      setNewCustomCategory('');
      setShowCustomCategoryInput(false);
      setCategoryError('');
    }
  }, [visible]);

  return (
    <Drawer
      title="Create Tournament"
      open={visible}
      onClose={handleClose}
      width="40vw"
      footer={[
        <FooterRow key="actions">
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} disabled={isSubmitting}>
            Create Tournament
          </Button>
        </FooterRow>,
      ]}
    >
      <ScrollArea ref={scrollRef}>
        <Form
          form={form}
          layout="vertical"
          validateTrigger={hasSubmitted ? 'onChange' : 'onSubmit'}
        >
          <FormSection>
            <StyledFormItem
              name="sport"
              label="Sport"
              rules={[{ required: true, message: 'Please select sport' }]}
            >
              <StyledSelect
                placeholder="Select sport"
                onChange={onSportChange}
                allowClear
              >
                {sportOptions.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </StyledSelect>
            </StyledFormItem>
            <StyledFormItem
              name="gender"
              label="Gender"
              rules={[{ required: true, message: 'Please select gender' }]}
            >
              <StyledSelect
                placeholder="Select gender"
                onChange={onGenderChange}
                allowClear
              >
                {genderOptions.map((g) => (
                  <Option key={g.value} value={g.value}>
                    {g.label}
                  </Option>
                ))}
              </StyledSelect>
            </StyledFormItem>
            <StyledFormItem
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select category' }]}
            >
              <StyledSelect placeholder="Select category" allowClear>
                {categoryOptions.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </StyledSelect>
            </StyledFormItem>
            {!showCustomCategoryInput && (
              <AddCategoryButton
                onClick={() => setShowCustomCategoryInput(true)}
              >
                <PlusCircleFilled /> Add Custom Category
              </AddCategoryButton>
            )}
            {showCustomCategoryInput && (
              <CustomCategoryWrapper>
                <CustomCategoryContainer>
                  <Input
                    value={newCustomCategory}
                    onChange={(e) => {
                      setNewCustomCategory(e.target.value);
                      setCategoryError('');
                    }}
                    placeholder="Custom category"
                  />
                  <Button type="primary" onClick={addCustomCategory}>
                    Add
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCustomCategoryInput(false);
                      setCategoryError('');
                      setNewCustomCategory('');
                    }}
                  >
                    Cancel
                  </Button>
                </CustomCategoryContainer>
                {categoryError && <ErrorText>{categoryError}</ErrorText>}
              </CustomCategoryWrapper>
            )}
            <StyledFormItem
              name="type"
              label="Tournament Type"
              rules={[{ required: true, message: 'Please select type' }]}
            >
              <StyledSelect placeholder="Select type" allowClear>
                {uiTournamentTypeOptions.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.label}
                  </Option>
                ))}
              </StyledSelect>
            </StyledFormItem>
            <StyledFormItem
              name="fee"
              label="Tournament Fee"
              rules={[{ required: true, message: 'Please enter fee' }]}
            >
              <StyledInput
                type="number"
                placeholder="Enter fee"
                min="0"
                step="1"
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === '+') {
                    e.preventDefault();
                  }
                }}
              />
            </StyledFormItem>
            <DateRow>
              <StyledFormItem
                name="startDate"
                label="Start Date"
                rules={[
                  { required: true, message: 'Please select start date' },
                ]}
              >
                <StyledDatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  disabledDate={(date: Dayjs) => {
                    if (!date) return false;
                    if (
                      eventStartDayJS &&
                      date.isBefore(eventStartDayJS, 'day')
                    ) {
                      return true;
                    }

                    if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) {
                      return true;
                    }

                    return false;
                  }}
                  suffixIcon={<BlueCalendarIcon />}
                  onChange={(date) => {
                    const endDate = form.getFieldValue('endDate');
                    if (endDate && date && endDate.isBefore(date, 'day')) {
                      form.setFieldsValue({ endDate: null });
                    }
                  }}
                />
              </StyledFormItem>
              <StyledFormItem
                name="endDate"
                label="End Date"
                rules={[
                  { required: true, message: 'Please select end date' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('startDate');
                      if (!value || !startDate) return Promise.resolve();
                      if (value.isBefore(startDate, 'day')) {
                        return Promise.reject(
                          new Error('End date cannot be before start date'),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <StyledDatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  disabledDate={(date: Dayjs) => {
                    if (!date) return false;
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && date.isBefore(startDate, 'day')) {
                      return true;
                    }
                    if (eventEndDayJS && date.isAfter(eventEndDayJS, 'day')) {
                      return true;
                    }

                    return false;
                  }}
                  suffixIcon={<BlueCalendarIcon />}
                />
              </StyledFormItem>
            </DateRow>
          </FormSection>
        </Form>
      </ScrollArea>
    </Drawer>
  );
};

export default AddTournamentDrawer;

/* ---------------- styles ---------------- */

const ScrollArea = styled(Col)`
  height: 100%;
  overflow-y: auto;
  padding: 16px 24px;
`;

const FormSection = styled.div`
  padding: 16px;
  background: ${COLORS.white.primary};
  border-radius: 12px;
  margin-bottom: 20px;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 18px !important;
  .ant-form-item-control {
    display: flex;
    flex-direction: column;
  }
  .ant-form-item-explain-error {
    font-size: ${TEXT_SIZES.XS}px;
    line-height: 1.3;
    margin-top: 16px;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 43px !important;
    align-items: center !important;
  }
`;

const StyledInput = styled(Input)`
  height: 43px;
  input {
    height: 43px;
  }
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 43px;
  .ant-picker-input > input {
    height: 43px;
  }
`;

const BlueCalendarIcon = styled(CalendarOutlined)`
  color: ${COLORS.skyBlue.primary};
  font-size: ${TEXT_SIZES.MD}px;
`;

const FooterRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const AddCategoryButton = styled.div`
  color: ${COLORS.skyBlue.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const CustomCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CustomCategoryContainer = styled.div`
  display: flex;
  gap: 8px;
  input {
    min-width: 220px;
  }
`;

const ErrorText = styled.div`
  color: ${COLORS.red.primary};
  font-size: ${TEXT_SIZES.XS}px;
`;
